import logging from "utils/logging";
import utils from "./firebaseUtils";
import { writeBatch, collection, getDoc, getDocs, addDoc, onSnapshot, doc, updateDoc, deleteDoc, query, where } from "firebase/firestore";
import { getDownloadURL, ref, uploadString } from "firebase/storage";
import {
  EQUALTO,
  FIREBASE_COLLECTION_GAMES,
  FIREBASE_COLLECTION_PLAYERS,
  FIREBASE_PLAYER_FIELD_USERID,
} from "./constants";
import {
  loadDiffOfModelsForQuerySnapshot,
  loadModelForDocumentSnapshot,
  loadModelsForQuerySnapshot,
} from "./firebaseModels";

const log = logging.getLogger("firebase/player");
log.setLevel('info');

const getPlayerForUserInGame = async (userId, gameId) => {
  log.debug("Get player for user in game", userId, gameId);

  const playerCollection = collection(utils.db(),
    FIREBASE_COLLECTION_GAMES,
    gameId,
    FIREBASE_COLLECTION_PLAYERS
  );

  const playerQuery = query(playerCollection, where(FIREBASE_PLAYER_FIELD_USERID, EQUALTO, userId));

  const querySnap = await getDocs(playerQuery);

  log.debug("Query snapshot: ", querySnap.docs);

  const players = loadModelsForQuerySnapshot(querySnap);

  log.debug("Players found: ", players);

  if (players.length === 0) {
    return null;
  } else if (players.length === 1) {
    return players[0];
  } else {
    log.error(`Found multiple players in game ${gameId} with userId ${userId}`);
    throw new Error(
      `Found multiple players in game ${gameId} with userId ${userId}`
    );
  }
};

const createSignatureImage = async (gameId, playerId, dataURL) => {
  const storage = utils.storage();
  const signatureRef = ref(storage, `game/${gameId}/signatures/player_${playerId}.jpg`);
  const snapshot = await uploadString(signatureRef, dataURL, 'data_url');
  log.debug("Uploaded a data_url string!", snapshot);

  return signatureRef;
};

const createPlayerForUserInGame = async (
  userId,
  gameId,
  playerName,
  signatureDataURL
) => {
  let existingPlayer = await getPlayerForUserInGame(userId, gameId);
  if (existingPlayer !== null) {
    return existingPlayer;
  }

  const playerCollection = collection(utils.db(),
    FIREBASE_COLLECTION_GAMES,
    gameId,
    FIREBASE_COLLECTION_PLAYERS
  );
  
  const playerRef = await addDoc(playerCollection, {
    userId,
    name: playerName,
    score: 0,
    playerState: {
      INSTRUCTIONS: {
        state: "NOT_READY"
      }
    }
  });

  const playerSnap = await getDoc(playerRef);

  log.debug("Got player: ", playerSnap.data());

  const signatureRef = await createSignatureImage(
    gameId,
    playerSnap.id,
    signatureDataURL
  );

  log.debug("Got signature ref: ", signatureRef);

  const url = await getDownloadURL(signatureRef);
  log.debug("Got signature file URL: ", url);

  await updateDoc(playerRef, {
    signatureURL: url,
    signatureStoragePath: signatureRef.fullPath,
  });

  return await loadModelForDocumentSnapshot(playerSnap);
};

/**
 * Watches players for a specific game
 * @param {string} gameId - The game ID
 * @param {function} onChange - The function to call when the players change
 * @returns {Promise<listener>}
 */
const watchPlayersForGameId = (gameId, onChange) => {
  log.debug("Watching players for game: ", gameId);

  const playersRef = collection(utils.db(),
    FIREBASE_COLLECTION_GAMES,
    gameId,
    FIREBASE_COLLECTION_PLAYERS
  );

  return onSnapshot(playersRef, async (qSnap) => {
    let diff = loadDiffOfModelsForQuerySnapshot(qSnap);
    log.debug("Got diffed players: ", diff);
    onChange(diff);
  });
};

/**
 * Updates a player's state for a specific game phase
 * 
 * @param {string} gameId - The game ID
 * @param {string} playerId - The player ID
 * @param {string} phase - The game phase (e.g., "INSTRUCTIONS")
 * @param {Object} stateData - The state data to update
 * @returns {Promise<Object>} - The updated player object
 */
const updatePlayerState = async (gameId, playerId, phase, stateData) => {
  log.debug(`Updating player ${playerId} state for ${phase}:`, stateData);

  const playerRef = doc(utils.db(),
    FIREBASE_COLLECTION_GAMES,
    gameId,
    FIREBASE_COLLECTION_PLAYERS,
    playerId
  );

  // Get current player data
  const playerSnap = await getDoc(playerRef);
  if (!playerSnap.exists()) {
    throw new Error(`Player ${playerId} not found in game ${gameId}`);
  }

  // Prepare update data
  const updateData = {
    [`playerState.${phase}`]: stateData
  };

  // Update the player document
  await updateDoc(playerRef, updateData);

  // Return the updated player
  const updatedPlayerSnap = await getDoc(playerRef);
  return await loadModelForDocumentSnapshot(updatedPlayerSnap);
};

const resetScores = async (gameId) => {
  log.debug("Resetting scores");

  const playersRef = collection(utils.db(),
    FIREBASE_COLLECTION_GAMES,
    gameId,
    FIREBASE_COLLECTION_PLAYERS
  );

  const playersQSnap = await getDocs(playersRef);

  const batch = writeBatch(utils.db());

  playersQSnap.forEach((playerSnap) => {
    batch.update(playerSnap.ref, { score: 0 });
  });

  await batch.commit();
};

const removePlayerFromGame = async (gameId, playerId) => {
  
  const playerRef = doc(utils.db(),
    FIREBASE_COLLECTION_GAMES,
    gameId,
    FIREBASE_COLLECTION_PLAYERS,
    playerId
  );
  
  log.debug("Removing player from game: ", gameId, playerId, playerRef);
  
  const result = await deleteDoc(playerRef);

  log.debug("Player removed from game: ", gameId, playerId, result);
};

const updatePlayerScore = async (gameId, playerId, newScore) => {
  log.debug(`Updating player ${playerId} score to ${newScore}`);

  const playerRef = doc(utils.db(),
    FIREBASE_COLLECTION_GAMES,
    gameId,
    FIREBASE_COLLECTION_PLAYERS,
    playerId
  );

  await updateDoc(playerRef, { score: newScore });
};

const api = {
  getPlayerForUserInGame,
  createPlayerForUserInGame,
  watchPlayersForGameId,
  resetScores,
  updatePlayerState,
  removePlayerFromGame,
  updatePlayerScore,
};

export default api;
